import { profilePredicateRegistry } from "@/lib/kernel/profile-predicates/registry";
import type { StaticData } from "@/lib/kernel/profile-predicates/static-data";
import { magicInitiateChoiceFields } from "@/lib/rules/dnd5e/character/feat-choices/magic-initiate";
import type { ChoiceField } from "@/lib/rules/dnd5e/character/feat-choices/types";
import { DEFAULT_ABILITY_SCORE_CAP } from "@/lib/rules/dnd5e/character/hydration-folds/stat-cap-override";
import {
  LEVEL_CAP,
  xpForLevel,
} from "@/lib/rules/dnd5e/character/level-constants";
import {
  DND5E_ABILITY_SCORES,
  type Dnd5eAbilityScoreTag,
} from "@/lib/rules/dnd5e/vocabulary/ability-score";

export type HpAcquisition = "roll" | "average";

export type HpGain = {
  readonly mode: HpAcquisition;
  readonly dieValue: number;
  readonly conMod: number;
  readonly gain: number;
  readonly newMaxHp: number;
};

/** RAW fixed average HP for a hit die: ⌊die/2⌋ + 1 (d8→5, d10→6). */
export function averageHpFor(hitDie: number): number {
  return Math.floor(hitDie / 2) + 1;
}

/** Eligible to level up: below the cap AND enough XP for the next level. */
export function canLevelUp(level: number, currentXp: number): boolean {
  return level < LEVEL_CAP && currentXp >= xpForLevel(level + 1);
}

export type FeatConfigKind = "asi" | "skill-chooser" | "dependent-choice";

export type FeatOption = {
  readonly id: string;
  readonly label: string;
  readonly needsConfig: boolean;
  readonly configKind?: FeatConfigKind;
  readonly choiceFields?: readonly ChoiceField[];
};

export type FeatPool =
  | "origin"
  | "general"
  | "fighting_style"
  | "epic_boon"
  | "any";

/** Human-readable labels for offerable feats, keyed by profile id. This table is
 * the label source-of-truth for the picker — the profilePredicateRegistry does
 * not carry a display name (only the seed EntityDefinition does, and that
 * registry is not threaded here). Unknown ids fall back to a title-cased
 * derivation of the `profile_feat_` suffix. */
const FEAT_LABELS: Record<string, string> = {
  profile_feat_asi: "Ability Score Increase",
  profile_feat_magic_initiate: "Magic Initiate",
  profile_feat_magic_initiate_origin: "Magic Initiate (Origin)",
  profile_feat_tough: "Tough",
  profile_feat_alert: "Alert",
  profile_feat_skilled: "Skilled",
  profile_feat_style_archery: "Archery",
  profile_feat_style_defense: "Defense",
};

const FEAT_PREFIX_PATTERN = /^profile_feat_/;

export function labelFor(profileId: string): string {
  const known = FEAT_LABELS[profileId];
  if (known) {
    return known;
  }
  // Fallback: strip the `profile_feat_` prefix, title-case the words.
  return profileId
    .replace(FEAT_PREFIX_PATTERN, "")
    .split("_")
    .map((w) => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function configKindFor(
  id: string,
  staticData: StaticData<"dnd5e"> | undefined
): FeatConfigKind | undefined {
  const isAsi =
    (
      staticData as
        | (StaticData<"dnd5e"> & { isAbilityScoreImprovement?: boolean })
        | undefined
    )?.isAbilityScoreImprovement === true;
  if (isAsi) {
    return "asi";
  }
  if (id === "profile_feat_skilled") {
    return "skill-chooser";
  }
  if (
    id === "profile_feat_magic_initiate" ||
    id === "profile_feat_magic_initiate_origin"
  ) {
    return "dependent-choice";
  }
  return undefined;
}

function choiceFieldsFor(id: string): readonly ChoiceField[] | undefined {
  if (
    id === "profile_feat_magic_initiate" ||
    id === "profile_feat_magic_initiate_origin"
  ) {
    return magicInitiateChoiceFields();
  }
  return undefined;
}

/** Selectable feats for a slot pool, derived from the profilePredicateRegistry.
 * Any registered `profile_feat_*` profile whose `staticData.featCategory` equals
 * `pool` is offered automatically — no curated list. `needsConfig` is true only
 * for feats that require follow-up configuration metadata. */
export function offerableFeats(pool: FeatPool): FeatOption[] {
  return profilePredicateRegistry
    .all()
    .filter((p) => p.id.startsWith("profile_feat_"))
    .filter((p) => {
      const category = (p.staticData as StaticData<"dnd5e"> | undefined)
        ?.featCategory;
      return pool === "any" ? category !== undefined : category === pool;
    })
    .map((p) => {
      const staticData = p.staticData as StaticData<"dnd5e"> | undefined;
      const configKind = configKindFor(p.id, staticData);
      const choiceFields = choiceFieldsFor(p.id);

      return {
        id: p.id,
        label: labelFor(p.id),
        needsConfig: configKind !== undefined,
        ...(configKind ? { configKind } : {}),
        ...(choiceFields ? { choiceFields } : {}),
      };
    });
}

// ASI rules — mirror lib/rules/dnd5e/character/tools/select-feat.ts (kept in sync by tests).
const ASI_VALID = new Set<Dnd5eAbilityScoreTag>(DND5E_ABILITY_SCORES);
const ASI_TOTAL = 2;
const ASI_MAX_PER_ABILITY = 2;
const ASI_MAX_ABILITIES = 2;

/** Client-side mirror of the kernel's ASI validation, so the picker fails fast
 * before calling select_feat_5e. The kernel stays authoritative. */
export function validateAsiConfig(
  config: Record<string, number>,
  scores: Record<string, number>,
  caps: Record<string, number>
): { ok: true } | { ok: false; reason: string } {
  const entries = Object.entries(config);
  if (entries.some(([k]) => !ASI_VALID.has(k as Dnd5eAbilityScoreTag))) {
    return {
      ok: false,
      reason: "Choose valid abilities (str/dex/con/int/wis/cha).",
    };
  }
  if (entries.some(([, v]) => Number.isNaN(v) || v <= 0)) {
    return { ok: false, reason: "Bumps must be positive." };
  }
  if (entries.some(([, v]) => v > ASI_MAX_PER_ABILITY)) {
    return {
      ok: false,
      reason: `At most +${ASI_MAX_PER_ABILITY} to one ability.`,
    };
  }
  if (entries.length > ASI_MAX_ABILITIES) {
    return {
      ok: false,
      reason: `Spread across at most ${ASI_MAX_ABILITIES} abilities.`,
    };
  }
  if (entries.reduce((s, [, v]) => s + v, 0) !== ASI_TOTAL) {
    return { ok: false, reason: `Distribute exactly ${ASI_TOTAL} points.` };
  }
  for (const [ability, amount] of entries) {
    const currentScore = scores[ability] ?? 0;
    const cap = caps[ability] ?? DEFAULT_ABILITY_SCORE_CAP;
    // NaN guard: NaN ?? 0 is NaN, and `NaN > cap` is false — without this an
    // unparseable score would silently bypass the cap check.
    if (Number.isNaN(currentScore) || Number.isNaN(cap)) {
      return {
        ok: false,
        reason: `Invalid score for ${ability.toUpperCase()}.`,
      };
    }
    if (currentScore + amount > cap) {
      return {
        ok: false,
        reason: `${ability.toUpperCase()} can't exceed ${cap}.`,
      };
    }
  }
  return { ok: true };
}

// Background ASI rules — mirror lib/rules/dnd5e/character/tools/select-background-asi.ts's
// validateBackgroundAsiConfig (kept in sync by tests). 2024 SRD background ability-score
// increase: either +2 to one ability and +1 to a different ability, or +1 to three
// different abilities. Total always 3 points, max +2 to any single ability.
const BG_ASI_VALID = new Set<Dnd5eAbilityScoreTag>(DND5E_ABILITY_SCORES);
const BG_ASI_TOTAL = 3;
const BG_ASI_MAX_PER_ABILITY = 2;

export type BackgroundAsiValidation =
  | { ok: true }
  | { ok: false; reason: string };

/** Client-side mirror of the kernel's background ASI validation, so the wizard's
 * ability stepper fails fast before calling select_background_asi_5e. The kernel
 * (select-background-asi.ts) stays authoritative. */
export function validateBackgroundAsiConfig(
  config: Record<string, number>
): BackgroundAsiValidation {
  const entries = Object.entries(config);

  const invalidKeys = entries.filter(
    ([k]) => !BG_ASI_VALID.has(k as Dnd5eAbilityScoreTag)
  );
  if (invalidKeys.length > 0) {
    return {
      ok: false,
      reason: `Invalid ability keys: ${invalidKeys.map(([k]) => k).join(", ")}. Must be one of: str, dex, con, int, wis, cha.`,
    };
  }

  const nonPositive = entries.filter(([, v]) => Number.isNaN(v) || v <= 0);
  if (nonPositive.length > 0) {
    return {
      ok: false,
      reason: `Config values must be positive numbers (got: ${nonPositive.map(([k, v]) => `${k}:${v}`).join(", ")}).`,
    };
  }

  const overMax = entries.filter(([, v]) => v > BG_ASI_MAX_PER_ABILITY);
  if (overMax.length > 0) {
    return {
      ok: false,
      reason: `Config values must not exceed ${BG_ASI_MAX_PER_ABILITY} per ability (got: ${overMax.map(([k, v]) => `${k}:${v}`).join(", ")}).`,
    };
  }

  const total = entries.reduce((sum, [, v]) => sum + v, 0);
  if (total !== BG_ASI_TOTAL) {
    return {
      ok: false,
      reason: `Config must distribute exactly ${BG_ASI_TOTAL} points (got ${total}).`,
    };
  }

  const values = entries.map(([, v]) => v).sort((a, b) => a - b);
  const isTwoAbilitySplit =
    entries.length === 2 && values[0] === 1 && values[1] === 2;
  const isThreeAbilitySplit =
    entries.length === 3 && values.every((v) => v === 1);
  if (!(isTwoAbilitySplit || isThreeAbilitySplit)) {
    return {
      ok: false,
      reason:
        "Config must be either a +2/+1 split across exactly two abilities, or a +1/+1/+1 split across exactly three abilities.",
    };
  }

  return { ok: true };
}

/** Narrow the level_up_5e ack `data` to a typed HpGain, or null if absent/malformed. */
export function parseHpGain(data: unknown): HpGain | null {
  if (typeof data !== "object" || data === null || !("hpGain" in data)) {
    return null;
  }
  const g = (data as { hpGain?: unknown }).hpGain;
  if (typeof g !== "object" || g === null) {
    return null;
  }
  const r = g as Record<string, unknown>;
  // `typeof NaN === "number"`, so guard NaN explicitly on every numeric field —
  // a NaN would otherwise survive the narrowing and render as "NaN" in the reveal.
  const isNum = (v: unknown): v is number =>
    typeof v === "number" && !Number.isNaN(v);
  if (
    (r.mode !== "roll" && r.mode !== "average") ||
    !isNum(r.dieValue) ||
    !isNum(r.conMod) ||
    !isNum(r.gain) ||
    !isNum(r.newMaxHp)
  ) {
    return null;
  }
  return {
    mode: r.mode,
    dieValue: r.dieValue,
    conMod: r.conMod,
    gain: r.gain,
    newMaxHp: r.newMaxHp,
  };
}
